import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWrapper from "../utils/httpClient";
import { API_ORDER_QUERY, API_ORDER_QUERY_RESEND } from "../utils/apis";

export const resendOrderNotification = createAsyncThunk(
  'orderQuery/resendOrderNotification',
  async (guid, { rejectWithValue }) => {
    try {
      const url = `${API_ORDER_QUERY_RESEND}?guid=${guid}`;
      const response = await fetchWrapper.get(url, {});
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const exportOrderQuery = createAsyncThunk(
  'orderQuery/exportOrderQuery',
  async (queryParams = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value != null && value !== '') params.append(key, value);
      });

      const url = `${API_ORDER_QUERY}/export${params.toString() ? `?${params}` : ''}`;
      const blob = await fetchWrapper.getFile(url);

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `order-query-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return blob;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOrderQuery = createAsyncThunk(
  'orderQuery/fetchOrderQuery',
  async (queryParams = {}, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams();

      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] !== null && queryParams[key] !== undefined && queryParams[key] !== '') {
          queryString.append(key, queryParams[key]);
        }
      });

      const url = queryString.toString()
        ? `${API_ORDER_QUERY}?${queryString.toString()}`
        : API_ORDER_QUERY;

      const response = await fetchWrapper.get(url);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const orderQuerySlice = createSlice({
  name: 'orderQuery',
  initialState: {
    data: null,
    loading: false,
    error: null,
    queryParams: {},
    pagination: {
      pageNumber: 0,
      totalRecords: 0,
      totalPages: 0,
      pageSize: 10
    },
    exportLoading: false,
    resendStatus: {
      loading: false,
      error: null,
      success: false
    }
  },
  reducers: {
    clearOrderQuery: (state) => {
      state.data = null;
      state.error = null;
      state.queryParams = {};
      state.pagination = {
        pageNumber: 0,
        totalRecords: 0,
        totalPages: 0,
        pageSize: 10
      };
      state.resendStatus = {
        loading: false,
        error: null,
        success: false
      };
    },
    setQueryParams: (state, action) => {
      state.queryParams = { ...state.queryParams, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearResendStatus: (state) => {
      state.resendStatus = {
        loading: false,
        error: null,
        success: false
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderQuery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderQuery.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.resdata.content;
        state.pagination = {
          pageNumber: action.payload.pageNumber,
          totalRecords: action.payload.totalRecords,
          totalPages: action.payload.resdata.totalPages,
          pageSize: action.payload.resdata.size
        };
      })
      .addCase(fetchOrderQuery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
      })
      .addCase(exportOrderQuery.pending, (state) => {
        state.exportLoading = true;
      })
      .addCase(exportOrderQuery.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportOrderQuery.rejected, (state, action) => {
        state.exportLoading = false;
        state.error = action.payload;
      })
      .addCase(resendOrderNotification.pending, (state) => {
        state.resendStatus.loading = true;
        state.resendStatus.error = null;
        state.resendStatus.success = false;
      })
      .addCase(resendOrderNotification.fulfilled, (state) => {
        state.resendStatus.loading = false;
        state.resendStatus.success = true;
      })
      .addCase(resendOrderNotification.rejected, (state, action) => {
        state.resendStatus.loading = false;
        state.resendStatus.error = action.payload;
        state.resendStatus.success = false;
      });
  }
});

export const {
  clearOrderQuery,
  setQueryParams,
  setPagination,
  clearResendStatus
} = orderQuerySlice.actions;

export default orderQuerySlice.reducer;